<script setup lang="ts">
import { AI_ACCOUNT_CHOICES, orList } from '@zerohuman/shared'
import { Button, Input, ZhFieldError, ZhPanel, ZhSystemLabel } from '@zerohuman/ui'
import { computed, ref } from 'vue'
import type { AiAccountFailure, AiAccountProvider } from '@/api'
import { useFieldValidation } from '@/composables/useFieldValidation'
import type { SubscriptionProvider as PlanProvider } from '@/lib/agents'
import { required } from '@/lib/fieldRules'

/**
 * The secure connect card for a new user's AI account: a panel, never a chat message. A plan to sign in with, then a
 * key to paste, in this order: Connect Claude Pro/Max, pre-selected (#1719), a ChatGPT plan, a SuperGrok plan, then
 * an Anthropic, OpenAI, xAI or OpenRouter key (#1716). Directly under the options, one plain sentence says what the
 * chosen option will cost the user and which provider bills them for it (#1722), in every state before connecting.
 * The options' labels, hints and billing lines, and the plans the intro names, come from the shared list of AI account
 * choices (#2150); each plan's sign-in instructions stay here, since only the card shows them.
 *
 * A plan is the Agents page's own connection for that provider, not a second one: the card's owner starts the
 * provider's authorization and completes it with what the person pastes back, which is a code for Claude and, for
 * ChatGPT and SuperGrok, the address their browser was sent to. This card only asks for those two steps
 * (`start-subscription`, `complete-subscription`) and holds what was pasted in a password box that is never
 * autofilled, cleared as it is handed on and whenever the option changes. While a plan is selected there is no key box.
 *
 * A key is typed into a password box that is never autofilled, handed to the card's owner on submit and cleared as it
 * goes, and cleared again whenever the option changes. Nothing here renders a key or a code back: a failure is the
 * owner's plain words, never built from what was typed.
 *
 * When a key check itself did not go through (`unavailable`), the key may be fine, so the card offers the two ways on
 * beside the message (#1800): try again, which takes the owner back to the key box, and the other key kind, which
 * switches to it with an empty box. A failed authorization (`authorization`) offers its own two: try again, which
 * starts authorization afresh, and the same provider's API key, with an empty box. Neither sends anything.
 */
/** A plan to sign in with. Each is a sign-in with one provider (`PlanProvider`), as the connection is stored. */
type Plan = 'claude' | 'chatgpt' | 'supergrok'
type Option = Plan | AiAccountProvider

const props = defineProps<{
    /** Why the last attempt failed, in plain words; null when nothing has failed. */
    failure: AiAccountFailure | null
    /** A check, or a step of the authorization, is under way. */
    busy: boolean
    /**
     * The provider's authorization page once authorization has started, so the card can link to it (a blocked pop-up
     * is never a dead end) and ask for what it gives back; null before that.
     */
    authorizationUrl?: string | null
    /** Whose authorization that is. Claude's when not said, the only kind there was before the others. */
    authorizationFor?: PlanProvider | null
}>()

const emit = defineEmits<{
    connect: [account: { provider: AiAccountProvider; apiKey: string }]
    /** Start the provider's authorization (again). */
    'start-subscription': [provider: PlanProvider]
    /** What the person pasted back from the provider's page, whole: a code, or the address they were sent to. */
    'complete-subscription': [code: string]
}>()

/**
 * What each plan's sign-in says. Claude's page shows a code to paste. OpenAI sends the browser on to an address on
 * the person's own computer, where nothing is listening: the page does not load, and the address itself is what they
 * paste back. xAI's page tries that address itself, finds nothing there, and shows a code to paste into Grok Build:
 * that code is what they paste back here.
 */
const PLANS: Record<
    Plan,
    {
        provider: PlanProvider
        /** Who the person signs in with. */
        vendor: string
        /** The same provider's API key, offered when the sign-in fails. */
        key: AiAccountProvider
        intro: string
        field: string
        paste: string
    }
> = {
    claude: {
        provider: 'claude',
        vendor: 'Anthropic',
        key: 'anthropic',
        intro: "Anthropic's sign-in page opens in a new window. Approve the connection there, then paste the code it shows you back here.",
        field: 'Authorization code',
        paste: 'Paste the entire code Anthropic shows you, including the part after the #. It is stored encrypted and never shown again.',
    },
    chatgpt: {
        provider: 'openai',
        vendor: 'OpenAI',
        key: 'openai',
        intro: "OpenAI's sign-in page opens in a new window. Sign in there. Your browser is then sent to a page that does not load, which is expected: copy that page's whole address and paste it back here.",
        field: 'Address you were sent to',
        paste: 'Paste the whole address from the address bar, starting http://localhost:1455. The sign-in it carries is stored encrypted and never shown again.',
    },
    supergrok: {
        provider: 'grok',
        vendor: 'xAI',
        key: 'xai',
        intro: 'xAI\'s sign-in page opens in a new window. Sign in there. xAI then shows a code under "Enter this code to finish signing in" and says to paste it into Grok Build: copy that code and paste it back here instead.',
        field: 'Code xAI showed you',
        paste: "Paste the whole code from xAI's page. The sign-in it carries is stored encrypted and never shown again.",
    },
}

/**
 * One choice: what it is called, what to know about it, and, in one plain sentence, what the user will pay and to
 * whom (`billing`, #1722).
 */
type OptionEntry = { id: Option; label: string; hint: string; billing: string }

/**
 * The choices, in the order they are offered, with what each one is called and what to know about it: the shared
 * list (#2150), so the card, the notice, the reminder email and the chat word them the same. Its ids are the card's
 * plans and the API's key providers.
 */
const OPTIONS: readonly OptionEntry[] = AI_ACCOUNT_CHOICES.map(choice => ({
    id: choice.id,
    label: choice.label,
    hint: choice.hint,
    billing: choice.billing,
}))

/** The card's opening line, naming every plan of the shared list, then the keys. */
const INTRO = `Connect your ${orList(
    AI_ACCOUNT_CHOICES.filter(choice => choice.kind === 'plan').map(choice => choice.name),
)} plan, or use an API key if you have one.`

function isPlan(option: Option): option is Plan {
    return option in PLANS
}

/** The key kinds: a key failure's other-option offer moves between these and never to a plan. */
const KEY_OPTIONS = OPTIONS.filter((option): option is OptionEntry & { id: AiAccountProvider } => !isPlan(option.id))
const keyOption = (id: AiAccountProvider) =>
    KEY_OPTIONS.find(option => option.id === id) ?? (KEY_OPTIONS[0] as (typeof KEY_OPTIONS)[number])

const provider = ref<Option>('claude')
const apiKey = ref('')
const code = ref('')
/** Wraps the key box, so a retry can put the cursor back in it. */
const keyField = ref<HTMLLabelElement | null>(null)

/** The plan chosen, with how its sign-in reads; null while a key kind is chosen. */
const plan = computed(() => (isPlan(provider.value) ? { id: provider.value, ...PLANS[provider.value] } : null))
const keyProvider = computed((): AiAccountProvider | null => (isPlan(provider.value) ? null : provider.value))

/** What the chosen option will cost the user and who bills them, shown in every state before connecting (#1722). */
const billing = computed(() => OPTIONS.find(option => option.id === provider.value)?.billing ?? '')

/**
 * Where a key failure's other-option offer leads: OpenRouter, which routes to every provider, and from OpenRouter
 * an Anthropic key.
 */
const otherProvider = computed(() => keyOption(provider.value === 'openrouter' ? 'anthropic' : 'openrouter'))

/** An authorization failure belongs to the plan options, a key failure to the key options. */
const authorizationFailed = computed(() => props.failure?.reason === 'authorization')
const shownFailure = computed(() =>
    props.failure && authorizationFailed.value === (plan.value !== null) ? props.failure : null,
)

/** The authorization page of the plan chosen, when that is the one under way; another plan's is not shown. */
const planAuthorizationUrl = computed(() =>
    plan.value && (props.authorizationFor ?? 'claude') === plan.value.provider
        ? (props.authorizationUrl ?? null)
        : null,
)

/** The Connect action, until authorization is under way or has failed (then Try again starts it afresh). */
const showConnect = computed(() => !planAuthorizationUrl.value && !authorizationFailed.value)

/**
 * The key and the pasted code must not be blank (the rule Connect already applies), checked while typing (#1757).
 * Emptying a box after it is handed on is not the user's change, so it starts the box over.
 */
const keyFields = useFieldValidation(
    computed(() => ({ apiKey: apiKey.value })),
    { apiKey: required('The key is empty.', 'Paste the API key from your provider.') },
    { idPrefix: 'ai-account' },
)
const codeFields = useFieldValidation(
    computed(() => ({ code: code.value })),
    { code: required('Nothing is pasted yet.', 'Paste what the sign-in page gave you.') },
    { idPrefix: 'ai-account-plan' },
)

function clearKey(): void {
    apiKey.value = ''
    keyFields.reset()
}

function clearCode(): void {
    code.value = ''
    codeFields.reset()
}

function choose(next: Option): void {
    if (next !== provider.value) {
        clearKey()
        clearCode()
    }
    provider.value = next
}

/** Back to the key box to send it again; nothing is sent until the owner submits. */
function retry(): void {
    keyField.value?.querySelector('input')?.focus()
}

function submit(): void {
    const key = apiKey.value.trim()
    const kind = keyProvider.value
    if (keyFields.check() !== null || !kind) {
        return
    }
    // Out of the box before it is handed on, so whatever the check says, the key is not left on screen.
    clearKey()
    emit('connect', { provider: kind, apiKey: key })
}

function startSubscription(): void {
    if (!plan.value) {
        return
    }
    clearCode()
    emit('start-subscription', plan.value.provider)
}

function submitCode(): void {
    const pasted = code.value.trim()
    if (codeFields.check() !== null) {
        return
    }
    // Out of the box before it is handed on, so whatever the exchange says, the code is not left on screen.
    clearCode()
    emit('complete-subscription', pasted)
}
</script>

<template>
	<ZhPanel data-testid="ai-account-card" class="space-y-4">
		<ZhSystemLabel as="h2" tone="system">Connect your AI account</ZhSystemLabel>
		<p class="text-sm">{{ INTRO }}</p>
		<div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" role="radiogroup" aria-label="Kind of AI account">
			<button
				v-for="option in OPTIONS"
				:key="option.id"
				type="button"
				role="radio"
				:data-provider="option.id"
				:aria-checked="provider === option.id"
				:class="[
					'rounded-md border p-3 text-left text-sm transition-colors',
					provider === option.id ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted',
				]"
				@click="choose(option.id)"
			>
				<span class="block font-medium">{{ option.label }}</span>
				<span class="text-muted-foreground block text-xs">{{ option.hint }}</span>
			</button>
		</div>
		<p class="text-muted-foreground text-sm" data-testid="ai-account-billing">{{ billing }}</p>
		<template v-if="plan">
			<p v-if="!planAuthorizationUrl" class="text-muted-foreground text-xs">{{ plan.intro }}</p>
			<Button
				v-if="showConnect"
				type="button"
				:data-testid="`ai-account-${plan.id}-connect`"
				:disabled="busy"
				@click="startSubscription"
			>
				{{ OPTIONS.find(option => option.id === plan?.id)?.label }}
			</Button>
			<form v-if="planAuthorizationUrl" class="space-y-3" novalidate @submit.prevent="submitCode">
				<p class="text-muted-foreground text-xs">
					If no window opened,
					<a class="underline" :href="planAuthorizationUrl" target="_blank" rel="noopener noreferrer">
						open {{ plan.vendor }}'s page
					</a>
					yourself.
				</p>
				<label class="block text-sm">
					<span class="text-muted-foreground">{{ plan.field }}</span>
					<Input
						v-model="code"
						:data-testid="`ai-account-${plan.id}-code`"
						class="mt-1"
						type="password"
						autocomplete="off"
						spellcheck="false"
						v-bind="codeFields.bind('code')"
					/>
				</label>
				<ZhFieldError v-bind="codeFields.message('code')" />
				<p class="text-muted-foreground text-xs">{{ plan.paste }}</p>
				<Button type="submit" :disabled="busy">{{ busy ? 'Connecting…' : 'Finish connecting' }}</Button>
			</form>
		</template>
		<form v-else class="space-y-4" novalidate @submit.prevent="submit">
			<label ref="keyField" class="block text-sm">
				<span class="text-muted-foreground">Your key</span>
				<Input
					v-model="apiKey"
					data-testid="ai-account-key"
					class="mt-1"
					type="password"
					autocomplete="off"
					spellcheck="false"
					v-bind="keyFields.bind('apiKey')"
				/>
			</label>
			<ZhFieldError v-bind="keyFields.message('apiKey')" />
			<p class="text-muted-foreground text-xs">
				We check the key with one small real request before we save it. It is stored encrypted and never shown
				again.
			</p>
			<p v-if="shownFailure" role="alert" class="text-destructive text-sm" data-testid="ai-account-failure">
				{{ shownFailure.message }}
			</p>
			<div v-if="shownFailure?.reason === 'unavailable'" class="flex flex-wrap gap-2">
				<Button type="button" variant="outline" data-testid="ai-account-retry" @click="retry">Try again</Button>
				<Button type="button" variant="outline" data-testid="ai-account-other" @click="choose(otherProvider.id)">
					Use an {{ otherProvider.label }} instead
				</Button>
			</div>
			<Button type="submit" :disabled="busy">{{ busy ? 'Checking the key…' : 'Check and connect' }}</Button>
		</form>
		<template v-if="plan && shownFailure">
			<p role="alert" class="text-destructive text-sm" data-testid="ai-account-failure">
				{{ shownFailure.message }}
			</p>
			<div class="flex flex-wrap gap-2">
				<Button
					type="button"
					variant="outline"
					data-testid="ai-account-oauth-retry"
					:disabled="busy"
					@click="startSubscription"
				>
					Try again
				</Button>
				<Button type="button" variant="outline" data-testid="ai-account-oauth-other" @click="choose(plan.key)">
					Use an {{ keyOption(plan.key).label }} instead
				</Button>
			</div>
		</template>
	</ZhPanel>
</template>
